import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Mail, Phone, MapPin, Send, Calendar, Globe } from "lucide-react";
import { contactInfo } from "../data/mockData";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import emailjs from "@emailjs/browser";

const MAX_NAME = 80;
const MAX_SUBJECT = 120;
const MAX_MESSAGE = 1500;
const MIN_MESSAGE = 20; // éviter "salut" vide etc.
const MIN_DELAY_MS = 1500; // anti bot: doit rester un petit moment sur la page

// simple E-Mail-Check + Wegwerf-Domains
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
const DISPOSABLE_DOMAINS = [
  "yopmail.com",
  "mailinator.com",
  "tempmail.com",
  "10minutemail.com",
  "sharklasers.com",
];

const ContactSection = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    language: "",
    subject: "",
    message: "",
    sessionType: "",
  });

  // état d'UI
  const [errors, setErrors] = useState({});
  const [statusMsg, setStatusMsg] = useState("");
  const [statusType, setStatusType] = useState(""); // "success" | "error" | ""
  const [isSending, setIsSending] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const statusRef = useRef(null);

  // anti-bot
  const formReadyAtRef = useRef(Date.now());
  const [botField, setBotField] = useState(""); // honeypot caché
  // Scroll zum Status + Auto-Dismiss nach ein paar Sekunden
  useEffect(() => {
    if (!statusMsg) return;

    // A11y: Fokus auf die Meldung
    statusRef.current?.focus();
    statusRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });

    setShowToast(true);

    const timer = setTimeout(() => setShowToast(false), 6000);
    return () => clearTimeout(timer);
  }, [statusMsg]);


  // helpers
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Fehler für dieses Feld beim Tippen zurücksetzen
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleSelectChange = (name) => (value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const validate = () => {
    const newErrors = {};

    // name
    if (!formData.name.trim()) {
      newErrors.name = "Nom requis / مطلوب الاسم";
    } else if (formData.name.length > MAX_NAME) {
      newErrors.name = `Max ${MAX_NAME} caractères`;
    }

    // email
    const email = formData.email.trim();
    if (!email) {
      newErrors.email = "Email requis";
    } else {
      const domain = email.split("@")[1]?.toLowerCase() || "";
      const isDisposable = DISPOSABLE_DOMAINS.includes(domain);

      if (!emailRegex.test(email)) {
        newErrors.email = "Email invalide";
      } else if (isDisposable) {
        newErrors.email =
          "Merci d’utiliser une adresse personnelle (pas de mail jetable).";
      }
    }

    // phone (optionnel mais on bloque html/js malicieux)
    if (formData.phone) {
      const digits = formData.phone.replace(/\D/g, "");
      const phonePattern = /^[0-9+\s().-]+$/;

      if (!phonePattern.test(formData.phone)) {
        newErrors.phone =
          "Merci d’utiliser uniquement chiffres, espace, +, - ou ().";
      } else if (digits.length < 6 || digits.length > 16) {
        newErrors.phone = "Numéro de téléphone peu plausible.";
      }
    }

    // language
    if (!formData.language) {
      newErrors.language = "Langue requise";
    }

    // sessionType
    if (!formData.sessionType) {
      newErrors.sessionType = "Choisir un domaine";
    }

    // subject
    if (!formData.subject.trim()) {
      newErrors.subject = "Sujet requis";
    } else {
      if (formData.subject.length > MAX_SUBJECT) {
        newErrors.subject = `Max ${MAX_SUBJECT} caractères`;
      }
      // pas juste "hi"
      if (formData.subject.trim().length < 5) {
        newErrors.subject = "Sujet trop court";
      }
    }

    // message
    const cleanMsg = formData.message.trim();
    if (!cleanMsg) {
      newErrors.message = "Message requis";
    } else {
      if (cleanMsg.length < MIN_MESSAGE) {
        newErrors.message = `Min ${MIN_MESSAGE} caractères`;
      }
      if (cleanMsg.length > MAX_MESSAGE) {
        newErrors.message = `Max ${MAX_MESSAGE} caractères`;
      }
      // éviter liens et spam bruts
      const hasURL =
        /https?:\/\/|www\./i.test(cleanMsg) ||
        cleanMsg.includes("<") ||
        cleanMsg.includes(">");
      if (hasURL) {
        newErrors.message = "Pas de liens / No links please";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /** SUBMIT **/
  const handleSubmit = async (e) => {
    e.preventDefault();
     setStatusType("success");
    setStatusMsg("contact.thanks");
    setStatusType("");
    if (isSending) return;

    // Anti-bot 1 : honeypot
    if (botField.trim() !== "") {
      setStatusType("error");
      setStatusMsg("Spam détecté.");
      return;
    }

    // Anti-bot 2 : temps minimal sur le formulaire
    const elapsed = Date.now() - formReadyAtRef.current;
    if (elapsed < MIN_DELAY_MS) {
      setStatusType("error");
      setStatusMsg("Merci, réessayez dans un instant.");
      return;
    }

    if (!validate()) {
      setStatusType("error");
      setStatusMsg("Merci de corriger les champs en rouge.");
      return;
    }

    setIsSending(true);

    try {
      await emailjs.send(
        "service_iqxapw4", // service ID EmailJS
        "template_qovvu9d", // template ID EmailJS
        {
          // Header-/Template-Variablen
          subject: formData.subject || "Nouvelle demande",
          from_name: formData.name, // {{from_name}}
          visitor_name: formData.name, // {{visitor_name}} dans le corps
          reply_to: formData.email,

          // Contenu
          message: formData.message,
          phone: formData.phone || "",
          language: formData.language,
          sessionType: formData.sessionType || "",
        },
        "3s2p4bPR1mtkYzUJ6" // Public Key EmailJS
      );

      // Notification claire pour le visiteur
      setStatusType("success");
      setStatusMsg(t("contact.thanks"));

      setFormData({
        name: "",
        email: "",
        phone: "",
        language: "",
        subject: "",
        message: "",
        sessionType: "",
      });
      setErrors({});
      formReadyAtRef.current = Date.now(); // reset anti-bot timer
    } catch (err) {
      console.error("Email Send Error:", err);
      setStatusType("error");
      setStatusMsg(
        "❌ Erreur d’envoi. Merci de réessayer ou d’écrire directement : contact@mindup-coaching.com"
      );
    } finally {
      setIsSending(false);
    }
  };

  return (
    <section id="contact" className="py-12 bg-cream-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-20">
          {/* (Titel kommt vermutlich aus i18n im Hero / Sektion davor) */}
          <div className="w-24 h-1 bg-warm-sage mx-auto rounded-full" />
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* FORMULAIRE */}
          <Card className="p-8 bg-soft-gray/30 border-0">
            <h3 className="text-2xl font-light text-charcoal mb-8 tracking-wide text-center">
              {t("contact.formTitle")}
            </h3>

            <form
              onSubmit={handleSubmit}
              className="space-y-6"
              dir={isRTL ? "rtl" : "ltr"}
            >
              {/* Honeypot anti-bot (hidden) */}
              <input
                type="text"
                name="website"
                className="hidden"
                autoComplete="off"
                tabIndex="-1"
                value={botField}
                onChange={(e) => setBotField(e.target.value)}
              />

              <div className="grid md:grid-cols-2 gap-6">
                {/* Nom */}
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">
                    {t("contact.labels.name")}{" "}
                    <span className="text-warm-gray text-xs">
                      (max {MAX_NAME})
                    </span>
                  </label>
                  <Input
                    type="text"
                    name="name"
                    maxLength={MAX_NAME}
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder={t("contact.placeholders.name")}
                    required
                    className={`w-full ${
                      errors.name ? "border-red-500" : ""
                    }`}
                  />
                  {errors.name && (
                    <p className="text-xs text-red-500 mt-1">{errors.name}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">
                    {t("contact.labels.email")}
                  </label>
                  <Input
                    type="email"
                    name="email"
                    inputMode="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={t("contact.placeholders.email")}
                    required
                    className={`w-full ${
                      errors.email ? "border-red-500" : ""
                    }`}
                  />
                  {errors.email && (
                    <p className="text-xs text-red-500 mt-1">{errors.email}</p>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">
                    {t("contact.labels.phone")}
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    inputMode="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder={t("contact.placeholders.phone")}
                    className={`w-full ${
                      errors.phone ? "border-red-500" : ""
                    }`}
                    maxLength={40}
                  />
                  {errors.phone && (
                    <p className="text-xs text-red-500 mt-1">{errors.phone}</p>
                  )}
                </div>

                {/* Langue préférée */}
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">
                    {t("contact.labels.language")}
                  </label>
                  <Select
                    name="language"
                    value={formData.language}
                    onValueChange={handleSelectChange("language")}
                    required
                  >
                    <SelectTrigger
                      className={errors.language ? "border-red-500" : ""}
                    >
                      <SelectValue
                        placeholder={t("contact.placeholders.language")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fr">
                        {t("contact.options.fr")}
                      </SelectItem>
                      <SelectItem value="de">
                        {t("contact.options.de")}
                      </SelectItem>
                      <SelectItem value="ar">
                        {t("contact.options.ar")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.language && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.language}
                    </p>
                  )}
                </div>
              </div>

              {/* Domaine / sessionType */}
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">
                  {t("contact.labels.sessionType")}
                </label>
                <Select
                  name="sessionType"
                  value={formData.sessionType}
                  onValueChange={handleSelectChange("sessionType")}
                  required
                >
                  <SelectTrigger
                    className={errors.sessionType ? "border-red-500" : ""}
                  >
                    <SelectValue
                      placeholder={t("contact.placeholders.sessionType")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="personal">
                      {t("contact.sessions.personal")}
                    </SelectItem>
                    <SelectItem value="maternity">
                      {t("contact.sessions.maternity")}
                    </SelectItem>
                    <SelectItem value="business">
                      {t("contact.sessions.business")}
                    </SelectItem>
                    <SelectItem value="unsure">
                      {t("contact.sessions.unsure")}
                    </SelectItem>
                  </SelectContent>
                </Select>
                {errors.sessionType && (
                  <p className="text-xs text-red-500 mt-1">
                    {errors.sessionType}
                  </p>
                )}
              </div>

              {/* Sujet */}
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">
                  {t("contact.labels.subject")}{" "}
                  <span className="text-warm-gray text-xs">
                    (max {MAX_SUBJECT})
                  </span>
                </label>
                <Input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  placeholder={
                    t("contact.placeholders.subject") +
                    " — Pourquoi maintenant ?"
                  }
                  required
                  maxLength={MAX_SUBJECT}
                  className={`w-full ${
                    errors.subject ? "border-red-500" : ""
                  }`}
                />
                {errors.subject && (
                  <p className="text-xs text-red-500 mt-1">
                    {errors.subject}
                  </p>
                )}
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">
                  {t("contact.labels.message")}{" "}
                  <span className="text-warm-gray text-xs">
                    (min {MIN_MESSAGE} / max {MAX_MESSAGE})
                  </span>
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder={t("contact.placeholders.message")}
                  rows={5}
                  required
                  maxLength={MAX_MESSAGE}
                  className={`w-full resize-none ${
                    errors.message ? "border-red-500" : ""
                  }`}
                />
                <div className="flex justify-between text-xs mt-1 text-warm-gray">
                  <span>{errors.message && errors.message}</span>
                  <span>
                    {formData.message.length}/{MAX_MESSAGE}
                  </span>
                </div>
              </div>

              {/* Bouton Envoi */}
              <Button
                type="submit"
                size="lg"
                disabled={isSending}
                className={`
                  w-full bg-warm-sage hover:bg-warm-sage/90
                  text-cream-white font-medium py-4
                  transition-all duration-300 group
                  ${isSending ? "opacity-60 cursor-not-allowed" : ""}
                `}
              >
                {isSending ? "Envoi..." : t("contact.submit")}
                <Send
                  className={`${
                    isRTL ? "mr-2 -scale-x-100" : "ml-2"
                  } h-5 w-5 group-hover:translate-x-1 transition-transform`}
                />
              </Button>

         {statusMsg && showToast && (
                <div
                  ref={statusRef}
                  tabIndex={-1}
                  role="status"
                  aria-live="polite"
                  className={[
                    "mt-3 px-4 py-3 rounded-md text-sm font-medium border shadow-sm",
                    statusType === "success"
                      ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                      : "bg-red-50 text-red-700 border-red-300",
                  ].join(" ")}
                >
                  {statusMsg}
                </div>
              )}
            </form>

            {/* Bloc info réponse */}
            <div className="mt-8 p-6 bg-warm-sage/10 rounded-lg text-center">
              <Calendar className="h-8 w-8 text-warm-sage mx-auto mb-3" />
              <p className="text-charcoal font-medium mb-2">
                {t("contact.responseTitle")}
              </p>
              <p className="text-sm text-warm-gray">
                {t("contact.responseText")}
              </p>
            </div>
          </Card>

          {/* COLONNE DROITE : infos directes */}
          <div className="space-y-8" dir={isRTL ? "rtl" : "ltr"}>
            {/* Contact direct */}
            <Card className="p-8 bg-cream-white border-soft-gray">
              <h3 className="text-xl font-medium text-charcoal mb-6 tracking-wide">
                {t("contact.directTitle")}
              </h3>
              <div
                className={`space-y-4 ${isRTL ? "text-right" : ""}`}
              >
                <div
                  className={`flex items-center ${
                    isRTL ? "space-x-reverse space-x-3" : "space-x-3"
                  }`}
                >
                  <Mail className="h-5 w-5 text-warm-sage" />
                  <span className="text-charcoal">{contactInfo.email}</span>
                </div>
                <div
                  className={`flex items-center ${
                    isRTL ? "space-x-reverse space-x-3" : "space-x-3"
                  }`}
                >
                  <Phone className="h-5 w-5 text-warm-sage" />
                  <span className="text-charcoal">{contactInfo.phone}</span>
                </div>
              </div>
            </Card>

            {/* Modalités / lieux */}
            <Card className="p-8 bg-cream-white border-soft-gray">
              <h3 className="text-xl font-medium text-charcoal mb-6 tracking-wide">
                {t("contact.modalitiesTitle")}
              </h3>
              <div className="space-y-3">
                {contactInfo.locations.map((location, index) => (
                  <div
                    key={index}
                    className={`flex items-start ${
                      isRTL ? "space-x-reverse space-x-3" : "space-x-3"
                    }`}
                  >
                    <MapPin className="h-5 w-5 text-warm-sage mt-1 flex-shrink-0" />
                    <span className="text-charcoal">{location}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Langues */}
            <Card className="p-8 bg-cream-white border-soft-gray">
              <h3 className="text-xl font-medium text-charcoal mb-6 tracking-wide">
                {t("contact.languagesTitle")}
              </h3>
              <div
                className={`flex items-center ${
                  isRTL ? "space-x-reverse space-x-4" : "space-x-4"
                } mb-4`}
              >
                <Globe className="h-6 w-6 text-warm-sage" />
                <span className="text-charcoal font-medium">
                  {t("contact.intercultural")}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-4">
                {contactInfo.languages.map((language, index) => (
                  <div
                    key={index}
                    className="text-center p-3 bg-soft-gray/50 rounded-lg"
                  >
                    <span className="text-charcoal font-medium text-sm">
                      {language}
                    </span>
                  </div>
                ))}
              </div>
            </Card>

            {/* CTA final */}
            <div className="bg-warm-sage/10 p-8 rounded-lg text-center">
              <h4 className="text-xl font-medium text-charcoal mb-4">
                {t("contact.ctaTitle")}
              </h4>
              <p className="text-warm-gray mb-6 font-light">
                {t("contact.ctaText")}
              </p>
              <Button
                size="lg"
                onClick={() =>
                  document
                    .getElementById("contact")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className="bg-warm-sage hover:bg-warm-sage/90 text-cream-white font-medium px-8 py-3 rounded-md transition-all duration-300"
              >
                {t("contact.ctaButton")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
